import { Component, EventEmitter, Output, Input } from '@angular/core';
import { Observable, Subscription } from 'rxjs';
import { TypeaheadMatch } from 'ngx-bootstrap/typeahead/typeahead-match.class';
import { WatchAccountService } from '../../../../../services/watch-account.service';
import { AccountService } from '../../../../../services/account.service';
import {
  ILogon,
  ILogonData,
  IMutationData,
} from '../../../../../interfaces/graph-types';
import * as moment from 'moment';
import { ActivatedRoute, Router } from '@angular/router';

@Component({
  selector: 'audit-advanced-filter',
  template: require('./audit-advanced-filter.component.pug'),
  styles: [require('./audit-advanced-filter.component.css').toString()],
})
export class AuditAdvancedFilterComponent {
  @Output()
  onUpdateClick = new EventEmitter<any>();

  @Input()
  searchFilter;

  @Input()
  totalCount;

  logonData$: Observable<ILogonData[]>;
  selectedLogonId: number = null;

  sub: Subscription;
  mutationData: IMutationData[];
  filteredMutationData: IMutationData[];
  actionInput: String = null;
  selectedActions: IMutationData[] = [];

  filterApplied: boolean = false;
  isCollapsed: boolean = true;

  textSearch = '';

  today = new Date(Date.now()).setHours(0, 0, 0);
  placeholder = moment(this.today).format('L LT'); // placeholder matching bsDatePicker's format
  formatHelp = `Date format: "${this.placeholder}" (${
    moment.localeData()['_longDateFormat']['L']
  })`;
  maxDate = new Date();
  bsCreatedAfter: Date = null;
  bsCreatedBefore: Date = null;
  bsConfig = {
    dateInputFormat: 'L LT', // locale date and locale time
    containerClass: 'theme-default',
    isAnimated: true,
    returnFocusToInput: true,
    showWeekNumbers: false,
  };

  routeSub: Subscription;

  // -------------------------------------------------------------------
  constructor(
    private watchAccount: WatchAccountService,
    private accountService: AccountService,
    private router: Router,
    private route: ActivatedRoute,
  ) {
    this.maxDate.setDate(this.maxDate.getDate());
  }

  // -------------------------------------------------------------------
  ngOnInit() {
    const map = this.route.snapshot.queryParamMap;
    if (map.keys && map.keys.length > 0) {
      map.keys.forEach(key => {
        let value = map['params'][key];

        if (key == 'mutation_types') {
          value = map.getAll('mutation_types'); // use getAll because it returns an array of values even if there's only one
        }

        this.searchFilter[key] = value;
      });

      this.populateFilters();

      this.isCollapsed = false;
      this.apply();
    }

    this.logonData$ = this.accountService.auditsLogons(
      this.watchAccount.accountId().getValue(),
    );

    this.sub = this.accountService
      .auditsMutations(this.watchAccount.accountId().getValue())
      .subscribe(response => {
        if (response) {
          this.mutationData = response.auditsMutationData;
          this.filteredMutationData = this.mutationData;

          // populate mutation_types filter after loading async data
          if (this.searchFilter.mutation_types.length > 0) {
            this.searchFilter.mutation_types.forEach(type => {
              const action = this.mutationData.find(item => item.type == type);
              if (action) this.addAction(action);
            });
          }
        }
      });
  }

  // -------------------------------------------------------------------
  ngOnDestroy(): void {
    if (this.sub) this.sub.unsubscribe();

    if (this.routeSub) this.routeSub.unsubscribe();
  }

  // -------------------------------------------------------------------
  ngAfterViewInit() {}

  // -------------------------------------------------------------------
  populateFilters() {
    if (this.searchFilter.textSearch) {
      this.textSearch = this.searchFilter.textSearch;
    }
    if (this.searchFilter.logon_id) {
      this.selectedLogonId = +this.searchFilter.logon_id;
    }
    if (this.searchFilter.created_after) {
      this.bsCreatedAfter = new Date(+this.searchFilter.created_after * 1000);
    }
    if (this.searchFilter.created_before) {
      this.bsCreatedBefore = new Date(+this.searchFilter.created_before * 1000);
    }
  }

  // -------------------------------------------------------------------
  onActionSelect(event: TypeaheadMatch): void {
    if (!event.item) return;

    this.actionInput = '';
    this.addAction(event.item);
    this.searchFilter.mutation_types.push(event.item.type);
  }

  // -------------------------------------------------------------------
  // handle display only
  addAction(action) {
    this.selectedActions.push(action);
    this.filteredMutationData = this.filteredMutationData.filter(
      item => item.type && item.type !== action.type,
    );
  }

  // -------------------------------------------------------------------
  onActionRemove(action: IMutationData) {
    this.removeAction(action);
    this.searchFilter.mutation_types = this.selectedActions.map(
      item => item.type,
    ); //.filter((item) => item);
  }

  // -------------------------------------------------------------------
  // handle display only
  removeAction(action) {
    this.selectedActions = this.selectedActions.filter(
      data => data.type && data.type !== action.type,
    );
    this.filteredMutationData.push(action);
    this.filteredMutationData.sort((a, b) => b.count - a.count);
  }

  // -------------------------------------------------------------------
  textSearchChanged(newvalue) {
    this.searchFilter.textSearch = newvalue;
  }

  // -------------------------------------------------------------------
  public trackByFn(index, doc) {
    return doc.id; // unique id corresponding to the item
  }

  // -------------------------------------------------------------------
  changeSelectedLogon(value) {
    this.selectedLogonId = null
    this.searchFilter.logon_id = null;
    if (value) {
      this.selectedLogonId = value
      this.searchFilter.logon_id = value;
    }
  }

  // -------------------------------------------------------------------
  onCreatedAfterValueChange(value: any): void {
    if (value) {
      this.searchFilter.created_after = value.valueOf() / 1000;
    } else {
      this.searchFilter.created_after = null;
    }
  }

  // -------------------------------------------------------------------
  onCreatedBeforeValueChange(value: any): void {
    if (value) {
      this.searchFilter.created_before = value.valueOf() / 1000;
    } else {
      this.searchFilter.created_before = null;
    }
  }

  // -------------------------------------------------------------------
  updateRoute(queryParams = null) {
    // const params = JSON.parse(JSON.stringify(this.searchFilter))
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: queryParams,
    });
  }

  // -------------------------------------------------------------------
  cancel() {
    this.selectedLogonId = null;
    this.searchFilter.logon_id = null;
    this.searchFilter.textSearch = null;

    this.actionInput = null;
    this.selectedActions = [];
    this.searchFilter.mutation_types = [];
    this.filteredMutationData = this.mutationData;

    this.bsCreatedAfter = null;
    this.searchFilter.created_after = null;
    this.bsCreatedBefore = null;
    this.searchFilter.created_before = null;

    this.updateRoute();
    if (this.filterApplied) {
      this.onUpdateClick.emit();
    }
    this.filterApplied = false;
  }

  // -------------------------------------------------------------------
  apply() {
    this.updateRoute(this.searchFilter);
    this.onUpdateClick.emit(this.searchFilter);
    this.filterApplied = true;
  }

  // -------------------------------------------------------------------
  buttonsReady() {
    if (!this.mutationData || !this.logonData$) return false;

    if (
      this.searchFilter.logon_id ||
      this.searchFilter.mutation_types.length > 0 ||
      this.searchFilter.created_after ||
      this.searchFilter.created_before ||
      this.searchFilter.textSearch
    )
      return true;

    return false;
  }
}
